import api from "../api";

// Format dates to match Laravel format (dd/mm/yyyy)
const formatDateForLaravel = (dateStr) => {
  if (!dateStr) return "";

  // If already in Laravel format (dd/mm/yyyy), return as is
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  // Convert from yyyy-mm-dd to dd/mm/yyyy
  try {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  } catch (e) {
    console.error("Date parsing error:", e);
    return dateStr; // Return original if parsing fails
  }
};

// Format dates from Laravel format (dd/mm/yyyy) to React format (yyyy-mm-dd)
const formatDateForReact = (dateStr) => {
  if (!dateStr) return "";

  // If already in React format (yyyy-mm-dd), return as is
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
    return dateStr;
  }

  // Convert from dd/mm/yyyy to yyyy-mm-dd
  try {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  } catch (e) {
    console.error("Date parsing error:", e);
    return dateStr; // Return original if parsing fails
  }
};

// Mock data functions at the top (following existing pattern)
const getMockReportsData = () => {
  return {
    sections: [
      {
        title: "Guardians",
        reports: [
          {
            name: "Monthly Registered Guardians",
            route: "registered-guardians",
            requiresDateRange: true,
            description:
              "Report of guardians registered within a specific date range",
          },
        ],
      },
      {
        title: "Finances",
        reports: [
          {
            name: "Potential Earnings from Guardian Report",
            route: "potential-earnings",
            requiresDateRange: true,
            description: "Projected earnings from guardian license fees",
          },
          {
            name: "Total Projected Licence Fees Report",
            route: "total-license-fees",
            requiresDateRange: true,
            params: { report: "total-license-fees" },
            description: "Total projected license fees for a date range",
          },
          {
            name: "Total License Fees - Actual Report",
            route: "total-license-fees",
            requiresDateRange: true,
            params: { report: "actual-license-fees", status: "paid" },
            description: "Actual license fees received for a date range",
          },
          {
            name: "Outstanding Licence Fees Report",
            route: "total-license-fees",
            requiresDateRange: true,
            params: { report: "license-fees-differences", status: "paid" },
            description: "Outstanding license fees that haven't been paid",
          },
          {
            name: "Potential Earnings from Vacant Spaces Report",
            route: "potential-earnings-vacant",
            requiresDateRange: false,
            description: "Potential earnings from vacant properties",
          },
          {
            name: "Petty Cash - Report",
            route: "petty-cash",
            requiresDateRange: true,
            description: "Petty cash transactions and summary",
          },
          {
            name: "Profit & Loss Per Property Report",
            route: "finances.profit_and_loss",
            requiresDateRange: false,
            isLink: true,
            description: "Profit and loss analysis per property",
          },
          {
            name: "Profit & Loss Report",
            route: "finances.profit_and_loss.create",
            requiresDateRange: false,
            isLink: true,
            description: "General profit and loss report",
          },
        ],
      },
      {
        title: "Properties",
        reports: [],
      },
      {
        title: "Clients",
        reports: [],
      },
    ],
  };
};

const getMockReportData = (reportType) => {
  const mockData = {
    "potential-earnings": [
      {
        guardianName: "John Doe",
        fee: 850.0,
        property: "123 Main St",
        dueDate: "2024-01-15",
      },
      {
        guardianName: "Jane Smith",
        fee: 750.0,
        property: "456 Oak Ave",
        dueDate: "2024-01-20",
      },
      {
        guardianName: "Mike Johnson",
        fee: 900.0,
        property: "789 Pine Rd",
        dueDate: "2024-01-25",
      },
    ],
    "potential-earnings-vacant": [
      { name: "Vacant Property A", rooms: 5, licence_fee: 1200.0 },
      { name: "Vacant Property B", rooms: 3, licence_fee: 800.0 },
      { name: "Vacant Property C", rooms: 7, licence_fee: 1500.0 },
    ],
    "total-license-fees": [
      {
        guardianName: "John Doe",
        dueDate: "2024-01-15",
        property: "123 Main St",
        fee: 850.0,
      },
      {
        guardianName: "Jane Smith",
        dueDate: "2024-01-20",
        property: "456 Oak Ave",
        fee: 750.0,
      },
      {
        guardianName: "Mike Johnson",
        dueDate: "2024-01-25",
        property: "789 Pine Rd",
        fee: 900.0,
      },
    ],
    "petty-cash": {
      "Office Supplies": 150.0,
      "Maintenance Materials": 300.0,
      Transportation: 75.0,
      Utilities: 200.0,
      Miscellaneous: 50.0,
    },
    "registered-guardians": [
      { firstName: "John", lastName: "Doe", email: "john.doe@example.com" },
      { firstName: "Jane", lastName: "Smith", email: "jane.smith@example.com" },
      {
        firstName: "Mike",
        lastName: "Johnson",
        email: "mike.johnson@example.com",
      },
    ],
  };

  return mockData[reportType] || [];
};

const generateCSV = (data, reportType) => {
  let csv = "";

  switch (reportType) {
    case "potential-earnings":
      csv = "Guardian Name,Fee\n";
      data.forEach((item) => {
        csv += `${item.guardianName},${item.fee}\n`;
      });
      break;
    case "potential-earnings-vacant":
      csv = "Property Name,Rooms,Licence Fee\n";
      data.forEach((item) => {
        csv += `${item.name},${item.rooms},${item.licence_fee}\n`;
      });
      break;
    case "total-license-fees":
      csv = "Guardian Name,Due Date,Property,Fee\n";
      data.forEach((item) => {
        csv += `${item.guardianName},${item.dueDate},${item.property},${item.fee}\n`;
      });
      break;
    case "petty-cash":
      csv = "Category,Amount\n";
      Object.entries(data).forEach(([category, amount]) => {
        csv += `${category},${amount}\n`;
      });
      break;
    case "registered-guardians":
      csv = "First Name,Last Name,Email\n";
      data.forEach((item) => {
        csv += `${item.firstName},${item.lastName},${item.email}\n`;
      });
      break;
    default:
      csv = "Data\n";
      data.forEach((item) => {
        csv += `${JSON.stringify(item)}\n`;
      });
  }

  return csv;
};

const generatePDF = (data, reportType) => {
  // In a real implementation, this would generate actual PDF content
  // For now, we'll return a simple text representation
  let content = `Report: ${reportType}\nGenerated: ${new Date().toISOString()}\n\n`;

  switch (reportType) {
    case "potential-earnings":
      content += "Guardian Earnings Report\n";
      data.forEach((item) => {
        content += `${item.guardianName}: £${item.fee}\n`;
      });
      break;
    case "potential-earnings-vacant":
      content += "Vacant Property Earnings Report\n";
      data.forEach((item) => {
        content += `${item.name}: £${item.licence_fee}\n`;
      });
      break;
    case "total-license-fees":
      content += "License Fees Report\n";
      data.forEach((item) => {
        content += `${item.guardianName}: £${item.fee} (Due: ${item.dueDate})\n`;
      });
      break;
    case "petty-cash":
      content += "Petty Cash Report\n";
      Object.entries(data).forEach(([category, amount]) => {
        content += `${category}: £${amount}\n`;
      });
      break;
    case "registered-guardians":
      content += "Registered Guardians Report\n";
      data.forEach((item) => {
        content += `${item.firstName} ${item.lastName}: ${item.email}\n`;
      });
      break;
    default:
      content += "Report Data\n";
      data.forEach((item) => {
        content += `${JSON.stringify(item)}\n`;
      });
  }

  return content;
};

const reportsService = {
  // Get reports index data
  getReportsIndex: async () => {
    try {
      console.log("Fetching reports index");
      const response = await api.get("/reports");
      console.log("Reports index response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching reports index:", error);
      console.log("Falling back to mock data");
      // Return mock data for development
      return {
        success: true,
        data: getMockReportsData(),
      };
    }
  },

  // Potential Earnings from Guardian Report
  getPotentialEarnings: async (params) => {
    try {
      console.log("Fetching potential earnings report:", params);

      const response = await api.post(
        "/reports/potential-earnings",
        {
          report: "potential-earnings",
          dateFrom: formatDateForLaravel(params.dateFrom),
          dateTo: formatDateForLaravel(params.dateTo),
          format: params.format,
        },
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching potential earnings report:", error);
      console.log("Falling back to mock data");
      // Generate mock file
      const data = getMockReportData("potential-earnings");
      if (params.format === "csv") {
        return new Blob([generateCSV(data, "potential-earnings")], {
          type: "text/csv",
        });
      } else {
        return new Blob([generatePDF(data, "potential-earnings")], {
          type: "application/pdf",
        });
      }
    }
  },

  // Potential Earnings from Vacant Spaces Report
  getPotentialEarningsVacant: async (params) => {
    try {
      console.log("Fetching potential earnings vacant report:", params);
      const response = await api.post(
        "/reports/potential-earnings-vacant",
        {
          report: "potential-earnings-vacant",
          format: params.format,
        },
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching potential earnings vacant report:", error);
      console.log("Falling back to mock data");
      // Generate mock file
      const data = getMockReportData("potential-earnings-vacant");
      if (params.format === "csv") {
        return new Blob([generateCSV(data, "potential-earnings-vacant")], {
          type: "text/csv",
        });
      } else {
        return new Blob([generatePDF(data, "potential-earnings-vacant")], {
          type: "application/pdf",
        });
      }
    }
  },

  // Total License Fees Report
  getTotalLicenseFees: async (params) => {
    try {
      console.log("Fetching total license fees report:", params);
      const requestData = {
        report: params.report || "total-license-fees",
        dateFrom: formatDateForLaravel(params.dateFrom),
        dateTo: formatDateForLaravel(params.dateTo),
        format: params.format,
      };

      // Add status if provided
      if (params.status) {
        requestData.status = params.status;
      }

      const response = await api.post(
        "/reports/total-license-fees",
        requestData,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching total license fees report:", error);
      console.log("Falling back to mock data");
      // Generate mock file
      const data = getMockReportData("total-license-fees");
      if (params.format === "csv") {
        return new Blob([generateCSV(data, "total-license-fees")], {
          type: "text/csv",
        });
      } else {
        return new Blob([generatePDF(data, "total-license-fees")], {
          type: "application/pdf",
        });
      }
    }
  },

  // Petty Cash Report
  getPettyCash: async (params) => {
    try {
      console.log("Fetching petty cash report:", params);
      const response = await api.post(
        "/reports/petty-cash",
        {
          report: "petty-cash",
          dateFrom: formatDateForLaravel(params.dateFrom),
          dateTo: formatDateForLaravel(params.dateTo),
          format: params.format,
        },
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching petty cash report:", error);
      console.log("Falling back to mock data");
      // Generate mock file
      const data = getMockReportData("petty-cash");
      if (params.format === "csv") {
        return new Blob([generateCSV(data, "petty-cash")], {
          type: "text/csv",
        });
      } else {
        return new Blob([generatePDF(data, "petty-cash")], {
          type: "application/pdf",
        });
      }
    }
  },

  // Registered Guardians Report
  getRegisteredGuardians: async (params) => {
    try {
      console.log("Fetching registered guardians report:", params);
      const response = await api.post(
        "/reports/registered-guardians",
        {
          dateFrom: formatDateForLaravel(params.dateFrom),
          dateTo: formatDateForLaravel(params.dateTo),
          format: params.format,
        },
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching registered guardians report:", error);
      console.log("Falling back to mock data");
      // Generate mock file
      const data = getMockReportData("registered-guardians");
      if (params.format === "csv") {
        return new Blob([generateCSV(data, "registered-guardians")], {
          type: "text/csv",
        });
      } else {
        return new Blob([generatePDF(data, "registered-guardians")], {
          type: "application/pdf",
        });
      }
    }
  },
};

export default reportsService;
